"use client";

import { useMemo, useSyncExternalStore } from "react";
import { getSelectedCoordsRaw, parseCoords } from "@/src/constant/serviceArea/serviceArea";
import { nearestOutletId } from "@/src/utils/products/resolveCatalog";
import type { OutletDetails } from "@/src/types/tenant";

/** Header dispatches these whenever the buyer switches or edits an address. */
function subscribe(onChange: () => void) {
  window.addEventListener("selected_city_changed", onChange);
  window.addEventListener("address_changed", onChange);
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener("selected_city_changed", onChange);
    window.removeEventListener("address_changed", onChange);
    window.removeEventListener("storage", onChange);
  };
}

/**
 * The one outlet this buyer shops from.
 *
 * Both server snapshots return `defaultOutletId` on purpose: localStorage does
 * not exist during SSR, so the server renders the primary outlet's catalogue.
 * That keeps the anonymous HTML identical for every visitor — which is what lets
 * the landing payload stay cached by `prefix` alone — and the buyer's own outlet
 * swaps in on hydration once an address is known. Same pattern as
 * `useServiceArea` and `useOutletDelivery`.
 */
export function useServingOutlet(
  outlets: OutletDetails[],
  defaultOutletId: string
): string {
  const rawCoords = useSyncExternalStore(subscribe, getSelectedCoordsRaw, () => null);

  // parseCoords must be memoized — getSnapshot has to return a referentially
  // stable value, so the raw string is what the store returns and the parse
  // happens here. See getSelectedCoordsRaw's comment.
  const buyerCoords = useMemo(() => parseCoords(rawCoords), [rawCoords]);

  return useMemo(
    () => nearestOutletId(outlets, buyerCoords, defaultOutletId),
    [outlets, buyerCoords, defaultOutletId]
  );
}
